<template>
  <v-card outlined class="mb-4">
    <v-card-text>
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="selectedWarehouse"
            :items="warehouses"
            item-text="name"
            item-value="id"
            return-object
            label="Polazno skladište"
            prepend-icon="mdi-warehouse"
            outlined
            dense
            hint="Odaberite skladište iz kojeg uzimate sirovine"
            persistent-hint
            @change="onWarehouseChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.location }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-3">
            <h3 class="text-h6">Artikli za proizvodnju</h3>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              small
              @click="addItem"
              :disabled="!selectedWarehouse"
            >
              <v-icon left small>mdi-plus</v-icon>
              Dodaj artikl
            </v-btn>
          </div>

          <v-alert v-if="!selectedWarehouse" type="info" outlined dense>
            Najprije odaberite skladište
          </v-alert>

          <v-card
            v-for="(item, index) in items"
            :key="index"
            outlined
            class="mb-3"
          >
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="5">
                  <v-autocomplete
                    v-model="item.item"
                    :items="availableItems"
                    item-text="name"
                    item-value="id"
                    return-object
                    label="Artikl"
                    prepend-icon="mdi-package-variant"
                    outlined
                    dense
                    clearable
                    :search-input.sync="item.search"
                    @change="emitChanges"
                  >
                    <template v-slot:item="{ item: availableItem }">
                      <v-list-item-content>
                        <v-list-item-title>{{ availableItem.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          Šifra: {{ availableItem.code }} |
                          Stanje: {{ availableItem.stock }} {{ availableItem.unit }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item: selectedItem }">
                      {{ selectedItem.name }} ({{ selectedItem.stock }} {{ selectedItem.unit }})
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="item.quantity"
                    type="number"
                    label="Količina"
                    outlined
                    dense
                    suffix="kg"
                    :rules="[rules.required, rules.positive, rules.maxStock(item)]"
                    :hint="item.item ? `Dostupno: ${item.item.stock} kg` : ''"
                    persistent-hint
                    @change="emitChanges"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    :value="calculateValue(item)"
                    label="Ukupna vrijednost"
                    outlined
                    dense
                    readonly
                    prefix="€"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="1">
                  <v-btn
                    icon
                    color="error"
                    @click="removeItem(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-if="items.length > 0" outlined color="grey lighten-4">
            <v-card-text>
              <v-row>
                <v-col cols="12" class="text-right">
                  <h3>Ukupna vrijednost sirovine: <span class="primary--text">{{ totalValue.toFixed(2) }} €</span></h3>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'SourceItemsSelector',
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      selectedWarehouse: null,
      items: [],

      // Mock data - u buducnosti ce doci iz API-ja
      warehouses: [
        { id: 1, name: 'Glavno skladište', location: 'Zagreb' },
        { id: 2, name: 'Skladište sirovine', location: 'Zagreb' },
        { id: 3, name: 'Skladište gotovih proizvoda', location: 'Split' },
        { id: 4, name: 'Hladnjača', location: 'Rijeka' }
      ],

      availableItems: [
        { id: 1, name: 'Junetina sirovina', code: 'JUN-001', stock: 150, unit: 'kg', pricePerKg: 12.50 },
        { id: 2, name: 'Soja sirovina', code: 'SOJ-001', stock: 200, unit: 'kg', pricePerKg: 3.20 },
        { id: 3, name: 'Svinjetina sirovina', code: 'SVI-001', stock: 100, unit: 'kg', pricePerKg: 8.90 },
        { id: 4, name: 'Mirodije mix', code: 'MIR-001', stock: 50, unit: 'kg', pricePerKg: 15.00 },
        { id: 5, name: 'Sol', code: 'SOL-001', stock: 80, unit: 'kg', pricePerKg: 0.50 },
        { id: 6, name: 'Papar crni', code: 'PAP-001', stock: 20, unit: 'kg', pricePerKg: 25.00 },
        { id: 7, name: 'Luk suh', code: 'LUK-001', stock: 60, unit: 'kg', pricePerKg: 1.80 },
        { id: 8, name: 'Češnjak', code: 'CES-001', stock: 30, unit: 'kg', pricePerKg: 8.50 }
      ],

      rules: {
        required: v => !!v || 'Obavezno polje',
        positive: v => v > 0 || 'Količina mora biti veća od 0',
        maxStock: (item) => (v) => {
          if (!item.item) return true
          return v <= item.item.stock || `Maksimalno dostupno: ${item.item.stock} kg`
        }
      }
    }
  },
  computed: {
    totalValue () {
      return this.items.reduce((sum, item) => {
        return sum + this.calculateValue(item)
      }, 0)
    }
  },
  watch: {
  },
  methods: {
    onWarehouseChange () {
      this.$emit('warehouse-selected', this.selectedWarehouse)
    },
    addItem () {
      this.items.push({
        item: null,
        quantity: 0,
        search: ''
      })
      this.emitChanges()
    },
    removeItem (index) {
      this.items.splice(index, 1)
      this.emitChanges()
    },
    calculateValue (item) {
      if (!item.item || !item.quantity) return 0
      return item.item.pricePerKg * item.quantity
    },
    emitChanges () {
      this.$emit('input', this.items)
    }
  },
  mounted () {
    // Initialize with one empty item
    if (this.items.length === 0) {
      this.addItem()
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
</style>
